import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import {
  Save,
  X,
  Loader2,
  Trash2,
  Plus,
  FileText,
  Shield,
} from "lucide-react";
import toast from "react-hot-toast";
import { uploadToStorage } from "../utils/uploadToStorage";
import DocumentsViewer from "../shared/documents/DocumentsViewer";
import {
  BABYSITTER_BASIC_SERVICES,
  BABYSITTER_EXTRA_SERVICES,
  sanitizeExtraServices,
} from "../shared/babysitterServices";

// Utilitários de Máscara
const maskCPF = (v) =>
  v
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1");
const maskCEP = (v) =>
  v
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{3})\d+?$/, "$1");
const maskPhone = (v) =>
  v
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{4})\d+?$/, "$1");

const tagsDisponiveis = [
  "Recém-nascido",
  "Bebês (1-2 anos)",
  "Pré-escolar",
  "Idade Escolar",
  "Autismo",
  "Necessidades Especiais",
];

const idiomasOpcoes = [
  "Português",
  "Inglês",
  "Espanhol",
  "Francês",
  "Libras",
  "Outro",
];

const servicosBasicos = BABYSITTER_BASIC_SERVICES;
const servicosOpcoes = BABYSITTER_EXTRA_SERVICES;

const niveisOpcoes = [
  "Iniciante (menos de 2 anos)",
  "Intermediário (2-5 anos)",
  "Experiente (5+ anos)",
];

const faixaEtariaOpcoes = [
  "0-2 anos (Bebês)",
  "3-5 anos (Pré-escolar)",
  "6-10 anos (Escolar)",
  "11+ anos (Pré-adolescente)",
];

export default function AdminEditForm({ userId, userType, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dados, setDados] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Busca dados básicos da tabela usuarios
      const { data: userData, error: userError } = await supabase
        .from("usuarios")
        .select("*")
        .eq("id", userId)
        .single();

      if (userError) throw userError;

      let extraData = {};
      if (userType === "baba") {
        const { data } = await supabase
          .from("usuarios_babas")
          .select("*")
          .eq("usuario_id", userId)
          .maybeSingle();
        extraData = data || {};
      } else {
        const { data } = await supabase
          .from("usuarios_clientes")
          .select("*")
          .eq("usuario_id", userId)
          .maybeSingle();
        extraData = data || {};
      }

      // Mapeia referências para campos individuais se existirem
      const flatRefs = {};
      if (extraData.referencias_dados) {
        flatRefs.referencia1Nome = extraData.referencias_dados[0]?.nome || "";
        flatRefs.referencia1Contato =
          extraData.referencias_dados[0]?.contato || "";
        flatRefs.referencia2Nome = extraData.referencias_dados[1]?.nome || "";
        flatRefs.referencia2Contato =
          extraData.referencias_dados[1]?.contato || "";
      }

      setDados({
        ...userData,
        ...extraData,
        ...flatRefs,
        documentos_url: (() => {
          if (typeof userData?.documentos_url === "string") {
            try {
              return JSON.parse(userData.documentos_url);
            } catch {
              return {};
            }
          }
          return userData?.documentos_url || {};
        })(),
        certificados_docs: Array.isArray(extraData.certificados_docs)
          ? extraData.certificados_docs
          : [],
        faixa_etaria_preferida: extraData.faixa_etaria_preferida || [],
        idiomas_falados: extraData.idiomas_falados || [],
        servicos_extras: sanitizeExtraServices(extraData.servicos_extras),
        area_atuacao: extraData.area_atuacao || [],
      });
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
      toast.error("Erro ao carregar dados do usuário.");
    } finally {
      setLoading(false);
    }
  }, [userId, userType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = type === "checkbox" ? checked : value;

    if (name === "cpf") val = maskCPF(value);
    if (name === "cep") val = maskCEP(value);
    if (name === "telefone") val = maskPhone(value);

    setDados((prev) => ({ ...prev, [name]: val }));
  };

  const toggleArrayItem = (field, item) => {
    setDados((prev) => {
      const arr = prev[field] || [];
      const newArr = arr.includes(item)
        ? arr.filter((i) => i !== item)
        : [...arr, item];
      return { ...prev, [field]: newArr };
    });
  };

  const handleAddCert = () => {
    setDados((prev) => ({
      ...prev,
      certificados_docs: [
        ...(prev.certificados_docs || []),
        { nome: "", url: "" },
      ],
    }));
  };

  const handleRemoveCert = (index) => {
    const newCerts = [...dados.certificados_docs];
    newCerts.splice(index, 1);
    setDados((prev) => ({ ...prev, certificados_docs: newCerts }));
  };

  const handleCertFile = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const { path, error } = await uploadToStorage(file, "certificados");
      if (error) throw error;
      const newCerts = [...dados.certificados_docs];
      newCerts[index].url = path;
      setDados((prev) => ({ ...prev, certificados_docs: newCerts }));
      toast.success("Arquivo de certificado anexado!");
    } catch (err) {
      toast.error("Erro no upload do certificado.");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Normaliza a estrutura de documentos_url para o padrão { frente: { path: "..." }, ... }
      let docUrlValue = dados.documentos_url;
      if (typeof docUrlValue === "string") {
        try {
          docUrlValue = JSON.parse(docUrlValue);
        } catch (e) {}
      }

      // Garante que é salvo como JSONB string se necessário, mas idealmente o Supabase lida com o objeto se a coluna for JSONB
      const finalDocs =
        typeof docUrlValue === "object"
          ? JSON.stringify(docUrlValue)
          : docUrlValue;

      // 1. Atualiza tabela usuarios
      const { error: err1 } = await supabase
        .from("usuarios")
        .update({
          nome: dados.nome,
          sobrenome: dados.sobrenome,
          email: dados.email,
          telefone: dados.telefone?.replace(/\D/g, ""),
          cidade: dados.cidade,
          endereco: dados.endereco,
          numero: dados.numero,
          bairro: dados.bairro,
          cep: dados.cep?.replace(/\D/g, ""),
          complemento: dados.complemento,
          bio: dados.bio,
          estado: dados.estado,
          banco: dados.banco,
          agencia: dados.agencia,
          conta: dados.conta,
          pix: dados.pix,
          foto_url: dados.foto_url,
          documentos_url: finalDocs,
        })
        .eq("id", userId);
      if (err1) throw err1;

      // 2. Atualiza tabela específica
      if (userType === "baba") {
        const { error: err2 } = await supabase.from("usuarios_babas").upsert({
          usuario_id: userId,
          cpf: dados.cpf?.replace(/\D/g, ""),
          data_nascimento: dados.data_nascimento,
          experiencia_text: dados.experiencia_text,
          qualificacoes_text: dados.qualificacoes_text,
          nivel_experiencia: dados.nivel_experiencia,
          raio_atendimento_km: dados.raio_atendimento_km,
          aceita_emergencial: dados.aceita_emergencial,
          rede_social: dados.rede_social,
          certificados_docs: dados.certificados_docs,
          disponibilidade: dados.disponibilidade,
          idiomas_falados: dados.idiomas_falados,
          servicos_extras: sanitizeExtraServices(dados.servicos_extras),
          area_atuacao: dados.area_atuacao,
          referencias_dados: [
            { nome: dados.referencia1Nome, contato: dados.referencia1Contato },
            { nome: dados.referencia2Nome, contato: dados.referencia2Contato },
          ],
        });
        if (err2) throw err2;
      } else {
        const { error: err3 } = await supabase
          .from("usuarios_clientes")
          .upsert({
            usuario_id: userId,
            cpf: dados.cpf?.replace(/\D/g, ""),
            data_nascimento: dados.data_nascimento,
            num_criancas: dados.num_criancas,
            caracteristicas_crianca: dados.caracteristicas_crianca,
            necessidades_especiais: dados.necessidades_especiais,
            descricao_necessidades: dados.descricao_necessidades,
            frequencia_servico: dados.frequencia_servico,
            faixa_etaria_preferida: dados.faixa_etaria_preferida,
          });
        if (err3) throw err3;
      }

      // Sincroniza o status de verificação global do usuário como true (edição via Admin)
      await supabase.rpc("alterar_verificada_usuarios", {
        p_user_id: userId,
        p_verificada: true,
      });

      toast.success("Perfil atualizado com sucesso!");
      if (onClose) onClose();
    } catch (err) {
      console.error("Erro ao salvar:", err);
      toast.error("Falha ao salvar alterações.");
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full rounded-lg border border-warm-300 bg-white px-3 py-2.5 text-sm text-warm-900 placeholder:text-warm-400 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:border-rose-400 transition";
  const lbl = "block mb-1 text-xs font-semibold text-warm-700";
  const tagCls = (active) => `rounded-full border px-3 py-1 text-xs font-medium cursor-pointer transition-colors ${active ? "border-rose-400 bg-rose-400/10 text-rose-400" : "border-warm-200 bg-white text-warm-700 hover:border-rose-400"}`;
  const SectionTitle = ({ icon, children }) => (
    <div className="col-span-full mt-4 first:mt-0">
      <h4 className="flex items-center gap-2 text-sm font-bold text-rose-400">
        {icon} {children}
      </h4>
      <hr className="mt-1 border-warm-100" />
    </div>
  );

  if (loading)
    return (
      <div className="p-10 text-center">
        <Loader2 className="animate-spin mx-auto text-rose-400" />
      </div>
    );

  return (
    <form onSubmit={handleSave} className="text-sm">
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">

        <SectionTitle>Dados Básicos</SectionTitle>

        <div><label className={lbl}>Nome</label><input name="nome" value={dados.nome || ""} onChange={handleChange} required className={inp} /></div>
        <div><label className={lbl}>Sobrenome</label><input name="sobrenome" value={dados.sobrenome || ""} onChange={handleChange} required className={inp} /></div>
        <div><label className={lbl}>E-mail</label><input name="email" value={dados.email || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>Telefone</label><input name="telefone" value={dados.telefone || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>CPF</label><input name="cpf" value={dados.cpf || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>Data Nascimento</label><input type="date" name="data_nascimento" value={dados.data_nascimento || ""} onChange={handleChange} className={inp} /></div>

        <div className="col-span-full">
          <label className={lbl}>Idiomas Falados</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {idiomasOpcoes.map((idioma) => (
              <button key={idioma} type="button" className={tagCls(dados.idiomas_falados?.includes(idioma))} onClick={() => toggleArrayItem("idiomas_falados", idioma)}>{idioma}</button>
            ))}
          </div>
        </div>

        <div className="col-span-full">
          <label className={lbl}>Áreas de Atuação / Especialidades</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {tagsDisponiveis.map((tag) => (
              <button key={tag} type="button" className={tagCls(dados.area_atuacao?.includes(tag))} onClick={() => toggleArrayItem("area_atuacao", tag)}>{tag}</button>
            ))}
          </div>
        </div>

        <SectionTitle>Endereço</SectionTitle>

        <div><label className={lbl}>CEP</label><input name="cep" value={dados.cep || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>Cidade</label><input name="cidade" value={dados.cidade || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>Estado (UF)</label><input name="estado" value={dados.estado || ""} onChange={handleChange} maxLength={2} className={inp} /></div>
        <div className="col-span-full"><label className={lbl}>Logradouro / Rua</label><input name="endereco" value={dados.endereco || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>Número</label><input name="numero" value={dados.numero || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>Bairro</label><input name="bairro" value={dados.bairro || ""} onChange={handleChange} className={inp} /></div>
        <div className="col-span-full"><label className={lbl}>Complemento</label><input name="complemento" value={dados.complemento || ""} onChange={handleChange} className={inp} /></div>

        <div className="col-span-full"><label className={lbl}>Apresentação (Bio)</label><textarea name="bio" value={dados.bio || ""} onChange={handleChange} rows={3} className={inp + " resize-none"} /></div>

        <SectionTitle>Dados Bancários</SectionTitle>

        <div><label className={lbl}>Banco</label><input name="banco" value={dados.banco || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>Agência</label><input name="agencia" value={dados.agencia || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>Conta</label><input name="conta" value={dados.conta || ""} onChange={handleChange} className={inp} /></div>
        <div><label className={lbl}>Chave PIX</label><input name="pix" value={dados.pix || ""} onChange={handleChange} className={inp} /></div>

        <SectionTitle icon={<Shield size={15} />}>Verificação de Identidade (Admin)</SectionTitle>

        <div className="col-span-full">
          <p className="mb-3 text-xs text-warm-500">
            Abra cada documento enviado para analisar antes de aprovar. A selfie é a foto do perfil.
          </p>
          <DocumentsViewer
            user={dados}
            role={userType === "baba" ? "baba" : "familia"}
          />
        </div>

        {/* CAMPOS ESPECÍFICOS: BABÁ */}
        {userType === "baba" && (
          <>
            <SectionTitle>Dados Profissionais (Babá)</SectionTitle>

            <div className="col-span-full"><label className={lbl}>Experiência Detalhada</label><textarea name="experiencia_text" value={dados.experiencia_text || ""} onChange={handleChange} rows={3} className={inp + " resize-none"} /></div>

            <div>
              <label className={lbl}>Nível de Experiência</label>
              <select name="nivel_experiencia" value={dados.nivel_experiencia || ""} onChange={handleChange} className={inp}>
                <option value="">Selecione o nível...</option>
                {niveisOpcoes.map((nivel) => <option key={nivel} value={nivel}>{nivel}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Raio Atendimento (km)</label><input type="number" name="raio_atendimento_km" value={dados.raio_atendimento_km || 0} onChange={handleChange} className={inp} /></div>

            <div className="col-span-full flex items-center gap-2">
              <input type="checkbox" id="aceita_emergencial" name="aceita_emergencial" checked={dados.aceita_emergencial || false} onChange={handleChange} className="accent-rose-400" />
              <label htmlFor="aceita_emergencial" className="text-xs font-medium text-warm-700 cursor-pointer">Aceita Emergencial?</label>
            </div>

            <div className="col-span-full mt-2">
              <label className={lbl}>Serviços básicos incluídos</label>
              <div className="flex flex-wrap gap-2 mt-1 mb-3">
                {servicosBasicos.map((service) => (
                  <span key={service} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    {service}
                  </span>
                ))}
              </div>

              <label className={lbl}>Serviços extras</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {servicosOpcoes.map((service) => (
                  <button
                    key={service}
                    type="button"
                    className={tagCls(dados.servicos_extras?.includes(service))}
                    onClick={() => toggleArrayItem("servicos_extras", service)}
                  >
                    {service}
                  </button>
                ))}
              </div>
            </div>

            <div className="col-span-full mt-2">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-bold text-rose-400">Certificados e Cursos</h4>
                <button type="button" onClick={handleAddCert} className="flex items-center gap-1 text-xs font-semibold text-rose-400 hover:underline">
                  <Plus size={14} /> Adicionar
                </button>
              </div>
              <hr className="mt-1 mb-3 border-warm-100" />
              <div className="flex flex-col gap-2">
                {(dados.certificados_docs || []).map((cert, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      placeholder="Nome do curso"
                      value={cert.nome}
                      className={inp + " flex-1"}
                      onChange={(e) => {
                        const newCerts = [...dados.certificados_docs];
                        newCerts[index].nome = e.target.value;
                        setDados((prev) => ({ ...prev, certificados_docs: newCerts }));
                      }}
                    />
                    <label className="cursor-pointer shrink-0" title="Anexar arquivo">
                      <FileText size={18} className={cert.url ? "text-emerald-500" : "text-warm-300"} />
                      <input type="file" hidden onChange={(e) => handleCertFile(e, index)} />
                    </label>
                    <button type="button" onClick={() => handleRemoveCert(index)} className="shrink-0">
                      <Trash2 size={16} className="text-red-400 hover:text-red-600" />
                    </button>
                  </div>
                ))}
                {(!dados.certificados_docs || dados.certificados_docs.length === 0) && (
                  <p className="text-xs text-center text-warm-400">Nenhum certificado cadastrado.</p>
                )}
              </div>
            </div>

            <SectionTitle>Referências Profissionais</SectionTitle>
            <div><label className={lbl}>Ref 1: Nome</label><input name="referencia1Nome" value={dados.referencia1Nome || ""} onChange={handleChange} className={inp} /></div>
            <div><label className={lbl}>Ref 1: Contato</label><input name="referencia1Contato" value={dados.referencia1Contato || ""} onChange={handleChange} className={inp} /></div>
          </>
        )}

        {/* CAMPOS ESPECÍFICOS: FAMÍLIA */}
        {userType === "cliente" && (
          <>
            <SectionTitle>Dados da Família</SectionTitle>

            <div><label className={lbl}>Número de Crianças</label><input type="number" name="num_criancas" value={dados.num_criancas || 0} onChange={handleChange} className={inp} /></div>

            <div className="flex items-center gap-2 self-end pb-2">
              <input type="checkbox" id="necessidades_especiais" name="necessidades_especiais" checked={dados.necessidades_especiais || false} onChange={handleChange} className="accent-rose-400" />
              <label htmlFor="necessidades_especiais" className="text-xs font-medium text-warm-700 cursor-pointer">Necessidades Especiais?</label>
            </div>

            {dados.necessidades_especiais && (
              <div className="col-span-full"><label className={lbl}>Descrição das Necessidades</label><textarea name="descricao_necessidades" value={dados.descricao_necessidades || ""} onChange={handleChange} rows={2} className={inp + " resize-none"} /></div>
            )}

            <div className="col-span-full">
              <label className={lbl}>Faixas Etárias das Crianças</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {faixaEtariaOpcoes.map((faixa) => (
                  <button key={faixa} type="button" className={tagCls(dados.faixa_etaria_preferida?.includes(faixa))} onClick={() => toggleArrayItem("faixa_etaria_preferida", faixa)}>{faixa}</button>
                ))}
              </div>
            </div>

            <div className="col-span-full"><label className={lbl}>Características e Rotina das Crianças</label><textarea name="caracteristicas_crianca" value={dados.caracteristicas_crianca || ""} onChange={handleChange} rows={3} className={inp + " resize-none"} /></div>
          </>
        )}
      </div>

      {/* Ações */}
      <div className="mt-6 flex gap-3 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 rounded-lg border border-warm-300 bg-white px-4 py-2 text-sm font-semibold text-warm-700 hover:bg-warm-50 transition-colors"
        >
          <X size={16} /> Cancelar
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-1.5 rounded-lg bg-rose-400 px-5 py-2 text-sm font-bold text-white hover:brightness-105 transition-all disabled:opacity-60"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
          Salvar Alterações
        </button>
      </div>
    </form>
  );
}
